import { calcularPendienteDevolver } from "./devolucion";
import { toJsDate } from "./fechas";

export const moneyNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const toDateSafe = (value) => toJsDate(value);

export const getPagosDeuda = (deuda = {}) => {
  if (Array.isArray(deuda.pagos)) return deuda.pagos;
  if (deuda.pagos && typeof deuda.pagos === "object") return Object.values(deuda.pagos);
  return [];
};

export const isSameDay = (a, b) => {
  const da = toDateSafe(a);
  const db = toDateSafe(b);
  if (!da || !db) return false;

  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};

export const isSameMonth = (a, b) => {
  const da = toDateSafe(a);
  const db = toDateSafe(b);
  if (!da || !db) return false;

  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth();
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfWeek = (date) => {
  const d = startOfDay(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() + (7 - day));
  d.setHours(23, 59, 59, 999);
  return d;
};

const isBetween = (value, start, end) => {
  const d = toDateSafe(value);
  if (!d) return false;
  return d.getTime() >= start.getTime() && d.getTime() <= end.getTime();
};

export const isDevolucionDeuda = (deuda = {}) => {
  const estado = String(deuda.estado || "").toLowerCase();
  const estatus = String(deuda.estatus || "").toLowerCase();

  return !!(
    deuda.devolucion === true ||
    deuda.esDevolucion === true ||
    estado.includes("devolucion") ||
    estatus.includes("devolucion") ||
    estado === "devuelta"
  );
};

export const calcularFinanzasDeuda = (deuda = {}, hoy = new Date()) => {
  const pagos = getPagosDeuda(deuda);
  const monto = moneyNumber(deuda.monto ?? deuda.importe);
  const enganche = moneyNumber(deuda.enganche);
  const mensualidad = moneyNumber(deuda.mensualidad ?? deuda.pagoMensual);
  const activo = deuda.activo !== false;
  const esDevolucion = isDevolucionDeuda(deuda);
  const nextDueDate = toDateSafe(deuda.nextDueDate);

  let totalPagado = 0;
  let totalDevuelto = 0;
  let engancheEnPagosFlag = false;
  let cobradoHoy = 0;
  let cobradoMes = 0;

  pagos.forEach((p) => {
    const cantidad = moneyNumber(p?.cantidad);
    const tipo = String(p?.tipo || "").toLowerCase();

    if (cantidad > 0) totalPagado += cantidad;
    if (cantidad < 0 && tipo === "devolucion_pago") totalDevuelto += Math.abs(cantidad);
    if (tipo === "enganche" && cantidad > 0) engancheEnPagosFlag = true;

    // "Cobrado" = dinero realmente cobrado (solo positivos). Las devoluciones
    // y ajustes (cantidad < 0) NO restan aquí — se rastrean en totalDevuelto;
    // de lo contrario un mes con devoluciones grandes dejaba el KPI en negativo.
    if (cantidad > 0 && isSameDay(p?.fecha, hoy)) cobradoHoy += cantidad;
    if (cantidad > 0 && isSameMonth(p?.fecha, hoy)) cobradoMes += cantidad;
  });

  // Sprint ENG-FIX (backwards-compat): tri-estado de engancheCobradoFuera.
  //   true       → suma (cobrado fuera explícito)
  //   false      → no suma (pactado pendiente explícito)
  //   undefined  → suma (legacy, comportamiento previo al cambio)
  // Si hay pago marcado tipo:enganche, ya está en totalPagado — no doblar.
  if (!engancheEnPagosFlag && deuda?.engancheCobradoFuera !== false && enganche > 0) {
    totalPagado += enganche;
  }

  const saldoVenta = Math.max(monto - totalPagado, 0);
  const pendienteDevolver = esDevolucion
    ? Math.max(moneyNumber(calcularPendienteDevolver(deuda)), 0)
    : 0;
  const saldoOperativo = esDevolucion ? pendienteDevolver : saldoVenta;

  const hoy0 = startOfDay(hoy);
  const diasMora = nextDueDate
    ? Math.max(Math.floor((hoy0.getTime() - startOfDay(nextDueDate).getTime()) / 86400000), 0)
    : 0;
  const vencida = activo && saldoOperativo > 0 && !!nextDueDate && diasMora > 0;
  const sinProximaFecha = activo && saldoOperativo > 0 && !nextDueDate;

  return {
    pagos,
    monto,
    enganche,
    mensualidad,
    totalPagado,
    totalDevuelto,
    saldoVenta,
    saldoOperativo,
    pendienteDevolver,
    cobradoHoy,
    cobradoMes,
    activo,
    esDevolucion,
    nextDueDate,
    diasMora,
    vencida,
    sinProximaFecha,
  };
};

export const construirResumenFinanciero = (deudasDocs = [], hoy = new Date()) => {
  const semanaFin = endOfWeek(hoy);
  const hoyInicio = startOfDay(hoy);
  const deudas = deudasDocs.map((docSnap) => {
    const data = docSnap.data ? docSnap.data() : docSnap;
    const id = docSnap.id || data.id;
    const fin = calcularFinanzasDeuda(data, hoy);
    const clienteNombre = [data.clienteNombre, data.clienteApellidos]
      .filter(Boolean)
      .join(" ")
      .trim();

    return {
      id,
      ...data,
      fin,
      clienteNombre: clienteNombre || data.nombreCliente || data.cliente || "Cliente sin nombre",
    };
  });

  const resumen = deudas.reduce(
    (acc, deuda) => {
      const { fin } = deuda;
      acc.cobradoHoy += fin.cobradoHoy;
      acc.cobradoMes += fin.cobradoMes;

      if (fin.activo && fin.saldoOperativo > 0 && !fin.esDevolucion) {
        acc.deudasActivas += 1;
        acc.saldoPendiente += fin.saldoOperativo;
      }

      if (fin.nextDueDate && isSameDay(fin.nextDueDate, hoy) && fin.activo) {
        acc.porCobrarHoy += fin.mensualidad;
      }

      if (
        fin.nextDueDate &&
        isBetween(fin.nextDueDate, hoyInicio, semanaFin) &&
        fin.activo
      ) {
        acc.porCobrarSemana += fin.mensualidad;
      }

      if (fin.vencida) {
        acc.cuentasEnMora += 1;
        acc.totalMora += fin.mensualidad || fin.saldoOperativo;
      }

      if (fin.esDevolucion && fin.pendienteDevolver > 0) {
        acc.devolucionesActivas += 1;
        acc.pendienteDevolver += fin.pendienteDevolver;
      }

      if (!fin.activo || fin.saldoOperativo <= 0) acc.deudasLiquidadas += 1;
      if (fin.sinProximaFecha) acc.sinProximaFecha += 1;
      if (fin.activo && fin.saldoOperativo > 0 && !fin.mensualidad) acc.sinMensualidad += 1;

      return acc;
    },
    {
      cobradoHoy: 0,
      cobradoMes: 0,
      porCobrarHoy: 0,
      porCobrarSemana: 0,
      saldoPendiente: 0,
      totalMora: 0,
      cuentasEnMora: 0,
      deudasActivas: 0,
      deudasLiquidadas: 0,
      devolucionesActivas: 0,
      pendienteDevolver: 0,
      sinProximaFecha: 0,
      sinMensualidad: 0,
    }
  );

  const topMora = deudas
    .filter((d) => d.fin.vencida)
    .sort((a, b) => b.fin.diasMora - a.fin.diasMora || b.fin.saldoOperativo - a.fin.saldoOperativo)
    .slice(0, 5);

  const topSaldos = deudas
    .filter((d) => d.fin.activo && d.fin.saldoOperativo > 0 && !d.fin.esDevolucion)
    .sort((a, b) => b.fin.saldoOperativo - a.fin.saldoOperativo)
    .slice(0, 5);

  const devoluciones = deudas
    .filter((d) => d.fin.esDevolucion && d.fin.pendienteDevolver > 0)
    .sort((a, b) => b.fin.pendienteDevolver - a.fin.pendienteDevolver)
    .slice(0, 5);

  const pagosRecientes = deudas
    .flatMap((d) =>
      d.fin.pagos.map((p) => ({
        ...p,
        deudaId: d.id,
        clienteId: d.clienteId,
        deudaFolio: d.folio || d.id,
        clienteNombre: d.clienteNombre,
        fechaDate: toDateSafe(p.fecha),
      }))
    )
    .filter((p) => p.fechaDate)
    .sort((a, b) => b.fechaDate - a.fechaDate)
    .slice(0, 6);

  const alertas = [
    resumen.cuentasEnMora > 0 && {
      tipo: "Mora",
      texto: `${resumen.cuentasEnMora} cuentas vencidas`,
      nivel: resumen.cuentasEnMora >= 8 ? "bad" : "warn",
    },
    resumen.devolucionesActivas > 0 && {
      tipo: "Devoluciones",
      texto: `${resumen.devolucionesActivas} devoluciones pendientes`,
      nivel: "warn",
    },
    resumen.sinProximaFecha > 0 && {
      tipo: "Seguimiento",
      texto: `${resumen.sinProximaFecha} deudas sin proxima fecha`,
      nivel: "warn",
    },
    resumen.sinMensualidad > 0 && {
      tipo: "Datos",
      texto: `${resumen.sinMensualidad} deudas activas sin mensualidad`,
      nivel: "warn",
    },
  ].filter(Boolean);

  return {
    resumen,
    topMora,
    topSaldos,
    devoluciones,
    pagosRecientes,
    alertas,
  };
};
